import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import MCPCapabilitiesTable from './MCPCapabilitiesTable';
import { McpMergedCapability } from '../../../../types/aiTypes';

type Props = {
  server?: any;
  authConfigs?: any[];
  health?: any;
  syncState?: any;
  capabilities: McpMergedCapability[];
  selectedCapability?: McpMergedCapability | null;
  onSelectCapability?: (capability: McpMergedCapability) => void;
  onEvaluateCapability?: (capability: McpMergedCapability) => void;
  onPromoteDiscovered?: (capability: McpMergedCapability) => void;
  promotingCapability?: boolean;
  onRefresh: () => void;
  onSync?: () => void;
  syncing?: boolean;
};

const statusColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
  const s = String(status || '').toLowerCase();
  if (s === 'healthy') return 'success';
  if (s === 'degraded') return 'warning';
  if (s === 'failed' || s === 'unhealthy') return 'error';
  return 'default';
};

const FieldGrid: React.FC<{ items: Array<{ label: string; value: any }> }> = ({ items }) => (
  <Grid container spacing={1.25}>
    {items.map((item) => (
      <React.Fragment key={item.label}>
        <Grid item xs={5}>
          <Typography variant="caption" color="text.secondary">
            {item.label}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {item.value === null || item.value === undefined || item.value === ''
              ? '-'
              : String(item.value)}
          </Typography>
        </Grid>
      </React.Fragment>
    ))}
  </Grid>
);

function effectiveAuthType(server: any, authConfigs: any[]) {
  const configuredAuth = authConfigs.find((auth) => auth?.auth_type && auth.auth_type !== 'none');
  return configuredAuth?.auth_type || server?.auth_type || 'none';
}

function credentialSummary(auth: any) {
  const credential = auth?.credential || {};

  if (auth?.auth_type === 'api_key') {
    const keys = Object.keys(credential);
    return keys.length ? `Header: ${keys[0]}` : 'API key stored securely';
  }

  if (auth?.auth_type === 'bearer') {
    return 'Bearer token stored securely';
  }

  if (auth?.auth_type === 'oauth_client_credentials') {
    return `Client ID: ${credential.client_id || '-'} / Token URL: ${credential.token_url || '-'}`;
  }

  return 'No authentication';
}

const MCPServerDetailPanel: React.FC<Props> = ({
                                                 server,
                                                 authConfigs = [],
                                                 health,
                                                 syncState,
                                                 capabilities,
                                                 selectedCapability,
                                                 onSelectCapability,
                                                 onEvaluateCapability,
                                                 onPromoteDiscovered,
                                                 promotingCapability,
                                                 onRefresh,
                                                 onSync,
                                                 syncing,
                                               }) => {
  if (!server) {
    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Select an MCP server to inspect status, configuration, discovery, and capabilities.
        </Typography>
      </Paper>
    );
  }

  const authType = effectiveAuthType(server, authConfigs);

  return (
    <Paper elevation={1} sx={{ p: 2.5 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
              {server.name}
            </Typography>

            <Chip size="small" variant="outlined" label={server.slug} />

            <Chip
              size="small"
              color={statusColor(health?.status || server.health_status)}
              label={String(health?.status || server.health_status || 'unknown')}
            />

            <Chip
              size="small"
              color={authType === 'none' ? 'default' : 'primary'}
              variant={authType === 'none' ? 'outlined' : 'filled'}
              label={`Auth: ${authType}`}
            />

            <Chip
              size="small"
              variant="outlined"
              label={`Configured ${server.configured_count ?? capabilities.filter((c) => c.configured).length}`}
            />

            <Chip
              size="small"
              variant="outlined"
              label={`Discovered ${server.discovered_count ?? capabilities.filter((c) => c.discovered).length}`}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-word' }}>
            {server.base_url}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
            Refresh
          </Button>

          {onSync ? (
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={onSync}
              disabled={!!syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Discovery'}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Server Configuration
            </Typography>

            <FieldGrid
              items={[
                { label: 'Type', value: server.server_type },
                { label: 'Transport', value: server.transport_type },
                { label: 'Auth Type', value: authType },
                { label: 'Enabled', value: String(server.enabled) },
                { label: 'Default', value: String(server.is_default) },
                { label: 'Priority', value: server.priority },
                { label: 'Timeout', value: server.timeout_seconds },
                { label: 'Health Path', value: server.healthcheck_path },
                { label: 'MCP Prefix', value: server.mcp_path_prefix },
                { label: 'Verify SSL', value: String(server.verify_ssl) },
              ]}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Health
            </Typography>

            <FieldGrid
              items={[
                { label: 'Status', value: health?.status || server.health_status || 'unknown' },
                { label: 'Last Check', value: health?.last_check_at },
                {
                  label: 'Response Time',
                  value: health?.response_time_ms ? `${health.response_time_ms} ms` : '-',
                },
                { label: 'Failures', value: health?.consecutive_failures },
                { label: 'Last Error', value: health?.last_error || '-' },
              ]}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Discovery Sync
            </Typography>

            <FieldGrid
              items={[
                { label: 'Status', value: syncState?.status || 'unknown' },
                { label: 'Last Sync', value: syncState?.last_sync_at },
                { label: 'Checksum', value: syncState?.sync_checksum || '-' },
                { label: 'Last Error', value: syncState?.last_error || '-' },
              ]}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Authentication Configurations
        </Typography>

        {authConfigs.length ? (
          <Grid container spacing={2}>
            {authConfigs.map((auth, idx) => (
              <Grid item xs={12} md={6} xl={4} key={auth.id || idx}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                  <Stack spacing={1}>
                    <Chip
                      size="small"
                      color={auth.auth_type === 'none' ? 'default' : 'primary'}
                      variant="outlined"
                      label={auth.auth_type || 'unknown'}
                    />

                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {credentialSummary(auth)}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Updated by: {auth.updated_by || '-'}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No auth configs configured.
          </Typography>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={700} mb={1}>
        Capabilities
      </Typography>

      <MCPCapabilitiesTable
        capabilities={capabilities}
        selectedCapabilityKey={selectedCapability?.key || null}
        onSelectCapability={onSelectCapability}
        onEvaluateCapability={onEvaluateCapability}
        onPromoteDiscovered={onPromoteDiscovered}
        promoting={promotingCapability}
      />
    </Paper>
  );
};

export default MCPServerDetailPanel;